import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, X, FileText, ExternalLink } from "lucide-react";
import { isImage, isVideo, type MediaItem } from "./types";

interface MediaLightboxProps {
  items: MediaItem[];
  /** Index of the open item, or null when closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

function MediaStage({ item }: { item: MediaItem }) {
  const { t } = useTranslation();
  if (isImage(item)) {
    return (
      <img
        src={item.url}
        alt={item.filename ?? ""}
        className="max-h-full max-w-full object-contain"
      />
    );
  }
  if (isVideo(item)) {
    return (
      <video
        src={item.url}
        poster={item.thumbnailUrl ?? undefined}
        controls
        autoPlay
        playsInline
        className="max-h-full max-w-full object-contain"
      />
    );
  }
  // DOCUMENT / unknown — offer to open in a new tab.
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg bg-white/5 px-8 py-10 text-white">
      <FileText className="h-12 w-12 text-white/70" />
      <span className="max-w-xs truncate text-sm text-white/80">{item.filename}</span>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20"
      >
        <ExternalLink className="h-4 w-4" /> {t("media.openInNewTab")}
      </a>
    </div>
  );
}

/**
 * Full-screen, keyboard-accessible media viewer. Esc and outside-click close
 * (Radix), ←/→ move between items, focus is trapped while open. Images are
 * contained, videos get full controls and autoplay, documents link out.
 */
export function MediaLightbox({ items, index, onIndexChange, onClose }: MediaLightboxProps) {
  const { t } = useTranslation();
  const open = index !== null && index >= 0 && index < items.length;

  const go = useCallback(
    (delta: number) => {
      if (index === null) return;
      const next = index + delta;
      if (next >= 0 && next < items.length) onIndexChange(next);
    },
    [index, items.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        go(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);

  if (!open || index === null) return null;
  const current = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const navBtn =
    "absolute top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30";

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col focus:outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            {current.filename ?? t("media.preview")}
          </DialogPrimitive.Title>

          {/* Top bar */}
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="truncate text-sm text-white/80">{current.filename}</span>
            <div className="flex items-center gap-3">
              {items.length > 1 && (
                <span className="text-xs tabular-nums text-white/55">
                  {index + 1} / {items.length}
                </span>
              )}
              <DialogPrimitive.Close
                aria-label={t("actions.close")}
                className="rounded-md p-1.5 text-white/80 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              >
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>
          </div>

          {/* Stage — clicking the empty backdrop closes. `min-h-0` lets the
              flex child shrink so a tall (portrait) video is contained instead
              of overflowing; padding is smaller on mobile for more room. */}
          <div
            className="relative flex flex-1 min-h-0 items-center justify-center overflow-hidden px-4 pb-4 sm:px-12 sm:pb-8"
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => go(-1)}
                disabled={!hasPrev}
                aria-label={t("media.previous")}
                className={`${navBtn} left-2`}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            {/* key remounts the element so videos/images reload on navigation */}
            <div
              key={current.id}
              className="flex min-h-0 max-h-full max-w-full items-center justify-center"
            >
              <MediaStage item={current} />
            </div>

            {items.length > 1 && (
              <button
                type="button"
                onClick={() => go(1)}
                disabled={!hasNext}
                aria-label={t("media.next")}
                className={`${navBtn} right-2`}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
